import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const PLATFORM_ITEMS = [
  {
    href: "/consumer-promo",
    label: "Consumer Promotions",
    description: "WhatsApp-led journeys, voucher logic, shopper engagement",
  },
  {
    href: "/trade-promo",
    label: "Trade Promotions",
    description: "Retail participation, execution visibility, incentive programs",
  },
  {
    href: "/display-receipt-ir",
    label: "Display IR & Receipt IR",
    description: "Image Recognition for shelf and receipt validation",
  },
];

const TECHNOLOGY_ITEMS = [
  {
    sectionId: "technology",
    label: "Analytics",
    description: "Dashboard views, KPIs, real-time reporting",
  },
  {
    sectionId: "technology",
    label: "Retail Execution",
    description: "Verification layers and structured outputs",
  },
];

const CUSTOMER_ITEMS = [
  {
    sectionId: "customers",
    label: "Testimonials",
    description: "Buyer proof and stakeholder perspective",
  },
  {
    sectionId: "news",
    label: "Latest News",
    description: "Recent updates, releases, and announcements",
  },
];

const EMPTY_SECTION_LINKS = [];

function Navbar({ onShowPage, sectionLinks = EMPTY_SECTION_LINKS, onSectionSelect }) {
  const handleSubpageSectionClick = (sectionId) => (event) => {
    event.preventDefault();
    closeMobileMenu();
    onSectionSelect?.(sectionId);
  };

  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isRootPage = typeof onShowPage === "function";
  const isHomePage = isRootPage && sectionLinks.length === 0;
  const isSubpage = !isRootPage && sectionLinks.length > 0;

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [isRootPage, sectionLinks.length]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleRootPageClick = (targetPage, sectionId) => (event) => {
    event.preventDefault();
    closeMobileMenu();
    onShowPage(targetPage, sectionId);
  };

  const handleBrandClick = (event) => {
    if (!isRootPage) {
      return;
    }

    event.preventDefault();
    closeMobileMenu();
    onShowPage("home");
  };

  const scrollToCta = () => {
    closeMobileMenu();
    document.getElementById("cta")?.scrollIntoView({ behavior: "smooth" });
  };

  const renderRootSectionLink = (sectionId, label) => {
    if (isRootPage) {
      return (
        <a href={`#${sectionId}`} onClick={handleRootPageClick("home", sectionId)}>
          {label}
        </a>
      );
    }

    return (
      <Link to="/" state={{ scrollTo: sectionId }}>
        {label}
      </Link>
    );
  };

  const renderMobileRootSectionLink = (sectionId, label, description) => {
    if (isRootPage) {
      return (
        <a href={`#${sectionId}`} onClick={handleRootPageClick("home", sectionId)}>
          {label}
          {description ? <small>{description}</small> : null}
        </a>
      );
    }

    return (
      <Link to="/" state={{ scrollTo: sectionId }} onClick={closeMobileMenu}>
        {label}
        {description ? <small>{description}</small> : null}
      </Link>
    );
  };

  const renderSubmenuSectionLink = (sectionId, label, description) => {
    if (isRootPage) {
      return (
        <a href={`#${sectionId}`} onClick={handleRootPageClick("home", sectionId)}>
          {label}
          <small>{description}</small>
        </a>
      );
    }

    return (
      <Link to="/" state={{ scrollTo: sectionId }}>
        {label}
        <small>{description}</small>
      </Link>
    );
  };

  return (
    <>
      <div className="nav-shell">
        <div className="nav">
          <div className="navin">
            {isRootPage ? (
              <a className="brand" href="#" onClick={handleBrandClick}>
                <img src="https://cdn.kar-tini.id/logo_96d3f51682.png" alt="Kartini Logo" />
              </a>
            ) : (
              <Link className="brand" to="/">
                <img src="https://cdn.kar-tini.id/logo_96d3f51682.png" alt="Kartini Logo" />
              </Link>
            )}

            <div className="links">
              <div className="menu-item">
                {renderRootSectionLink("platform", "Platform")}
                <div className="submenu">
                  {PLATFORM_ITEMS.map((item) => (
                    <Link key={item.href} to={item.href}>
                      {item.label}
                      <small>{item.description}</small>
                    </Link>
                  ))}
                </div>
              </div>
              <div className="menu-item">
                {renderRootSectionLink("technology", "Technology")}
                <div className="submenu">
                  {TECHNOLOGY_ITEMS.map((item) => (
                    <div key={`${item.label}-${item.sectionId}`}>
                      {renderSubmenuSectionLink(item.sectionId, item.label, item.description)}
                    </div>
                  ))}
                </div>
              </div>
              <div className="menu-item">
                {renderRootSectionLink("customers", "Customers")}
                <div className="submenu">
                  {CUSTOMER_ITEMS.map((item) => (
                    <div key={`${item.label}-${item.sectionId}`}>
                      {renderSubmenuSectionLink(item.sectionId, item.label, item.description)}
                    </div>
                  ))}
                </div>
              </div>
              {renderRootSectionLink("founders", "Leadership")}
            </div>

            <div className="actions">
              <button className="btn">EN</button>
              {isHomePage ? (
                <>
                  <button
                    className="btn"
                    onClick={() => {
                      closeMobileMenu();
                      onShowPage("signin");
                    }}
                  >
                    Sign In
                  </button>
                  <button
                    className="btn dark"
                    onClick={() => {
                      closeMobileMenu();
                      onShowPage("demo");
                    }}
                  >
                    Book Demo
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn"
                    onClick={() => {
                      closeMobileMenu();
                      navigate("/");
                    }}
                  >
                    Home
                  </button>
                  <button className="btn dark" onClick={scrollToCta}>
                    Book Demo
                  </button>
                </>
              )}
              <button
                className={`burger ${mobileMenuOpen ? "open" : ""}`}
                onClick={() => setMobileMenuOpen((open) => !open)}
                aria-label="Toggle mobile menu"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>
          </div>
        </div>

        {isSubpage ? (
          <div className="subpage-nav">
            <div className="subpage-navin">
              {sectionLinks.map((item) => (
                <button
                  key={item.id || item.href}
                  type="button"
                  className="subpage-nav-link"
                  onClick={handleSubpageSectionClick(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`} onClick={closeMobileMenu}>
        <div className="mobile-menu-content" onClick={(event) => event.stopPropagation()}>
          <button
            className="mobile-menu-close"
            onClick={closeMobileMenu}
            aria-label="Close mobile menu"
          >
            ×
          </button>
          <div className="mobile-menu-section">
            <h3>Platform</h3>
            {PLATFORM_ITEMS.map((item) => (
              <Link key={item.href} to={item.href} onClick={closeMobileMenu}>
                {item.label}
                <small>{item.description}</small>
              </Link>
            ))}
          </div>
          <div className="mobile-menu-section">
            <h3>Technology</h3>
            {TECHNOLOGY_ITEMS.map((item) => (
              <div key={`${item.label}-${item.sectionId}`}>
                {renderMobileRootSectionLink(item.sectionId, item.label, item.description)}
              </div>
            ))}
          </div>
          <div className="mobile-menu-section">
            <h3>Customers</h3>
            {CUSTOMER_ITEMS.map((item) => (
              <div key={`${item.label}-${item.sectionId}`}>
                {renderMobileRootSectionLink(item.sectionId, item.label, item.description)}
              </div>
            ))}
          </div>
          <div className="mobile-menu-section">
            {renderMobileRootSectionLink("founders", "Leadership")}
          </div>
          {isSubpage ? (
            <div className="mobile-menu-section">
              <h3>On This Page</h3>
              {sectionLinks.map((item) => (
                <a
                  key={item.id || item.href}
                  href={item.href || "#"}
                  onClick={handleSubpageSectionClick(item.id)}
                >
                  {item.label}
                </a>
              ))}
            </div>
          ) : null}
          <div className="mobile-menu-actions">
            <button className="btn">EN</button>
            {isHomePage ? (
              <>
                <button
                  className="btn"
                  onClick={() => {
                    closeMobileMenu();
                    onShowPage("signin");
                  }}
                >
                  Sign In
                </button>
                <button
                  className="btn dark"
                  onClick={() => {
                    closeMobileMenu();
                    onShowPage("demo");
                  }}
                >
                  Book Demo
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn"
                  onClick={() => {
                    closeMobileMenu();
                    navigate("/");
                  }}
                >
                  Home
                </button>
                <button className="btn dark" onClick={scrollToCta}>
                  Book Demo
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Navbar;
